import { type AxiosError, type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { debounce } from 'lodash-es';
import { baseControllers } from '@/axios/controllers';
import { useAuthStore } from '@/stores/auth';
import {
  ACCESS_TOKEN,
  LOGIN_ROUTE,
  REFRESH_ROUTE,
  REFRESH_TOKEN,
  TOKENS_EXPIRY_ERROR,
  TOKENS_INVALID_ERROR,
} from '@/types';
import { PEQUITY_BASE_URL } from '@/types/environment';
import { tokenExpired } from '@/utils/helpers';

const logout = debounce(
  async () => {
    // Abort all pending requests
    for (const controller of Object.values(baseControllers)) {
      try {
        if (controller) {
          controller.abort();
        }
      } catch {
        // noop
      }
    }

    const authStore = useAuthStore();
    const redirectPath = authStore.path;

    authStore.logout();

    if (redirectPath && redirectPath !== '/') {
      const urlParams = new URLSearchParams({ redirect: authStore.path });
      window.location.href = `/v2/welcome-back/?${urlParams.toString()}`;
    } else {
      window.location.href = '/v2/welcome-back/';
    }
  },
  1500,
  {
    leading: true,
    trailing: false,
  }
);

let pendingRefresh: Promise<string> | null = null;

const doRefresh = async (): Promise<string> => {
  const authStore = useAuthStore();
  return authStore.refreshToken();
};

const refreshToken = async (): Promise<string> => {
  if (pendingRefresh) return pendingRefresh;
  pendingRefresh = doRefresh().finally(() => {
    pendingRefresh = null;
  });
  return pendingRefresh;
};

const isPequityRequest = (url: string) => url.includes(PEQUITY_BASE_URL.split(':')[0]);
const isRefreshRequest = (url: string) => url.includes(REFRESH_ROUTE);

const getValidToken = async (): Promise<string | null> => {
  const token = localStorage.getItem(ACCESS_TOKEN);
  if (!token) return null;
  if (!tokenExpired(token)) return token;

  if (tokenExpired(localStorage.getItem(REFRESH_TOKEN) as string)) {
    throw Error(TOKENS_EXPIRY_ERROR);
  }

  try {
    await refreshToken();
    return localStorage.getItem(ACCESS_TOKEN);
  } catch {
    throw Error(TOKENS_INVALID_ERROR);
  }
};

export const useTokenInterceptors = (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    async (config) => {
      const url = new URL(config.url as string, config.baseURL).href;

      if (isPequityRequest(url)) {
        baseControllers[url] = new AbortController();
        config.signal = baseControllers[url].signal;

        if (!isRefreshRequest(url)) {
          const token = await getValidToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const config = error?.config;

      if (error?.message === TOKENS_EXPIRY_ERROR || error?.message === TOKENS_INVALID_ERROR) {
        logout();
        return Promise.reject(error);
      }

      let url;
      try {
        url = new URL(config?.url as string, config?.baseURL).href;
      } catch (err) {
        url = '';
      }

      if (url.includes(PEQUITY_BASE_URL.split(':')[0]) && error?.response?.status === 401) {
        if (url.includes(REFRESH_ROUTE)) {
          logout();
          return Promise.reject(error);
        }
        if ((error.config as AxiosRequestConfig & { _retry?: boolean })?._retry) {
          logout();
          return Promise.reject(error);
        }
        try {
          (error.config as AxiosRequestConfig & { _retry?: boolean })._retry = true;
          await refreshToken();
          return instance(error.config as AxiosRequestConfig);
        } catch {
          !url.includes(LOGIN_ROUTE) && logout();
        }
      }

      return Promise.reject(error);
    }
  );
};
